/**
 * LeadsKanban — flat kanban of clinic leads by status (Leads v2).
 * Columns come from the resolved status list; archive statuses
 * (converted / lost) render collapsed at the far side.
 * Drag a card onto a column → onMove(lead, statusValue). Dropping on
 * "lost" is handled by the parent (reason popover) via the same callback.
 * Pattern cloned from components/finance/PaymentKanban (2026 language).
 */
import { useState } from "react";
import { CaretLeft, CaretRight, Clock, Repeat, Plugs } from "@phosphor-icons/react";
import { getLeadSourceChannel } from "../../utils/leadContract";
import {
  statusStyleVars,
  displayPhone,
  getFollowUpState,
  FOLLOW_UP_LABELS,
  formatFollowUp,
  isArchivedStatus,
} from "./leadsUtils";

function LeadsKanban({
  leads,
  statuses,
  duplicateMap,
  identityKeyOf,
  onOpen,
  onMove,
  busy = false,
}) {
  const [dragging, setDragging] = useState(null); // { leadId, fromStatus }
  const [dragOver, setDragOver] = useState(null);
  const [expandedArchive, setExpandedArchive] = useState({});

  const columns = statuses.map((st) => ({
    ...st,
    archived: isArchivedStatus(st.value),
    items: leads.filter((l) => l.status === st.value),
  }));
  // Unknown statuses (doc value not in the list) get a trailing column so nothing disappears
  const known = new Set(statuses.map((s) => s.value));
  const unknownItems = leads.filter((l) => !known.has(l.status));
  if (unknownItems.length) {
    columns.push({ value: "__unknown", label: "לא ידוע", color: "#9ca3af", archived: false, items: unknownItems, unknown: true });
  }

  const handleDragStart = (e, lead) => {
    setDragging({ leadId: lead.id, fromStatus: lead.status });
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", lead.id);
    } catch {
      /* some browsers throw on setData for non-text */
    }
  };
  const handleDragEnd = () => {
    setDragging(null);
    setDragOver(null);
  };
  const handleDragOver = (e, col) => {
    if (!dragging || col.unknown) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOver !== col.value) setDragOver(col.value);
  };
  const handleDrop = (e, col) => {
    e.preventDefault();
    if (!dragging || col.unknown) return;
    const lead = leads.find((l) => l.id === dragging.leadId);
    setDragging(null);
    setDragOver(null);
    if (!lead || lead.status === col.value) return;
    onMove?.(lead, col.value);
  };

  return (
    <div className="ld-kanban" role="region" aria-label="לוח מתעניינים">
      {columns.map((col) => {
        const isCollapsed = col.archived && !expandedArchive[col.value];
        const canDrop = dragging && dragging.fromStatus !== col.value && !col.unknown;
        return (
          <div
            key={col.value}
            className={`ld-col${isCollapsed ? " is-collapsed" : ""}${dragOver === col.value && canDrop ? " drag-over" : ""}${col.archived ? " is-archive" : ""}`}
            style={statusStyleVars(col)}
            onDragOver={(e) => handleDragOver(e, col)}
            onDragLeave={() => setDragOver((v) => (v === col.value ? null : v))}
            onDrop={(e) => handleDrop(e, col)}
          >
            <div className="ld-col-header">
              {col.archived ? (
                <button
                  type="button"
                  className="ld-col-toggle"
                  onClick={() => setExpandedArchive((m) => ({ ...m, [col.value]: !m[col.value] }))}
                  title={isCollapsed ? "הצג" : "כווץ"}
                  aria-label={isCollapsed ? `הצג ${col.label}` : `כווץ ${col.label}`}
                  aria-expanded={!isCollapsed}
                >
                  {isCollapsed ? <CaretLeft size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                </button>
              ) : (
                <span className="ld-col-icon" aria-hidden="true" />
              )}
              <span className="ld-col-title">{col.label}</span>
              <span className="ld-col-count">{col.items.length}</span>
            </div>

            {!isCollapsed && (
              <div className="ld-col-body">
                {col.items.length === 0 ? (
                  <div className="ld-col-empty">{dragging && canDrop ? "שחרר כאן" : "אין מתעניינים"}</div>
                ) : (
                  col.items.map((lead) => {
                    const follow = getFollowUpState(lead);
                    const dup = duplicateMap && identityKeyOf ? (duplicateMap.get(identityKeyOf(lead)) || 0) > 1 : false;
                    const isWebhook = getLeadSourceChannel(lead) === "webhook";
                    return (
                      <div
                        key={lead.id}
                        className={`ld-kcard${dragging?.leadId === lead.id ? " dragging" : ""}${follow === "overdue" ? " is-overdue" : ""}`}
                        draggable={!busy && !col.archived}
                        onDragStart={(e) => handleDragStart(e, lead)}
                        onDragEnd={handleDragEnd}
                        onClick={() => onOpen?.(lead)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onOpen?.(lead);
                          }
                        }}
                      >
                        <div className="ld-kcard-row">
                          <span className="ld-kcard-name">{lead.name}</span>
                          {isWebhook && (
                            <span className="ld-kcard-src" title={`נקלט אוטומטית${lead.source?.keyLabel ? ` · ${lead.source.keyLabel}` : ""}`}>
                              <Plugs size={12} weight="bold" />
                            </span>
                          )}
                        </div>
                        {(lead.phone || lead.referralSource) && (
                          <div className="ld-kcard-meta">
                            {lead.phone && <span dir="ltr">{displayPhone(lead.phone)}</span>}
                            {lead.phone && lead.referralSource && <span className="ld-sep">·</span>}
                            {lead.referralSource && <span>{lead.referralSource}</span>}
                          </div>
                        )}
                        <div className="ld-kcard-chips">
                          {follow && (
                            <span className={`ld-chip ld-chip-follow ld-follow-${follow}`}>
                              <Clock size={11} weight="bold" />
                              {follow === "upcoming" ? formatFollowUp(lead) : FOLLOW_UP_LABELS[follow]}
                            </span>
                          )}
                          {dup && (
                            <span className="ld-chip ld-chip-dup">
                              <Repeat size={11} weight="bold" />
                              חוזר
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default LeadsKanban;
